import {
  ParametresToit,
  StructureToit,
  StyleBaleinePredefini,
  ProfilBaleinePersonnalise,
  DonneesStructureBasePourToit,
  TypeStyleBaleine
} from '../types';

/**
 * API du module Toit
 * Gère les communications avec le serveur pour la persistance et la validation
 * Conforme au cahier des charges - Architecture modulaire
 */
export class ToitAPI {
  private baseURL: string;

  constructor(baseURL: string = '/api/toit') {
    this.baseURL = baseURL;
  }

  /**
   * Sauvegarde les paramètres du toit
   */
  async sauvegarderParametres(parametres: ParametresToit): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/parametres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(parametres),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des paramètres du toit:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' };
    }
  }

  /**
   * Charge les paramètres du toit
   */
  async chargerParametres(projetId?: string): Promise<ParametresToit | null> {
    try {
      const url = projetId 
        ? `${this.baseURL}/parametres/${projetId}`
        : `${this.baseURL}/parametres`;
        
      const response = await fetch(url);

      if (!response.ok) {
        if (response.status === 404) {
          return null; // Pas de paramètres sauvegardés
        }
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres du toit:', error);
      return null;
    }
  }

  /**
   * Obtient la liste des styles prédéfinis disponibles
   */
  async obtenirStylesPredefinis(): Promise<StyleBaleinePredefini[]> {
    try {
      const response = await fetch(`${this.baseURL}/styles-predefinis`);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors du chargement des styles prédéfinis:', error);
      // Retourner des styles par défaut en cas d'erreur
      return this.obtenirStylesParDefaut();
    }
  }

  /**
   * Sauvegarde un profil personnalisé
   */
  async sauvegarderProfilPersonnalise(
    nom: string,
    profil: ProfilBaleinePersonnalise
  ): Promise<{ success: boolean; id?: string; message?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/profils-personnalises`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ nom, profil }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du profil personnalisé:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' };
    }
  }

  /**
   * Charge la liste des profils personnalisés
   */
  async chargerProfilsPersonnalises(): Promise<Array<{ id: string; nom: string; profil: ProfilBaleinePersonnalise; dateCreation: string }>> {
    try {
      const response = await fetch(`${this.baseURL}/profils-personnalises`);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors du chargement des profils personnalisés:', error);
      return [];
    }
  }

  /**
   * Supprime un profil personnalisé
   */
  async supprimerProfilPersonnalise(id: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/profils-personnalises/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la suppression du profil personnalisé:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' };
    }
  }

  /**
   * Valide la structure du toit côté serveur
   * Utilisé pour la validation croisée et la détection d'erreurs
   */
  async validerStructure(
    parametres: ParametresToit,
    donneesBase: DonneesStructureBasePourToit
  ): Promise<{ valide: boolean; erreurs?: string[]; avertissements?: string[] }> {
    try {
      const response = await fetch(`${this.baseURL}/valider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ parametres, donneesBase }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la validation de la structure:', error);
      return {
        valide: false,
        erreurs: ['Erreur de communication avec le serveur']
      };
    }
  }

  /**
   * Génère la structure du toit côté serveur (pour validation)
   * Note: La génération principale se fait côté client selon le cahier des charges
   */
  async genererStructureServeur(
    parametres: ParametresToit,
    donneesBase: DonneesStructureBasePourToit
  ): Promise<StructureToit | null> {
    try {
      const response = await fetch(`${this.baseURL}/generer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ parametres, donneesBase }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de la génération côté serveur:', error);
      return null;
    }
  }

  /**
   * Exporte la configuration du toit
   */
  async exporterConfiguration(
    parametres: ParametresToit,
    format: 'json' | 'xml' = 'json'
  ): Promise<{ success: boolean; data?: string; message?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/exporter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ parametres, format }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de l\'exportation:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' };
    }
  }

  /**
   * Importe une configuration du toit
   */
  async importerConfiguration(
    data: string,
    format: 'json' | 'xml' = 'json'
  ): Promise<{ success: boolean; parametres?: ParametresToit; message?: string }> {
    try {
      const response = await fetch(`${this.baseURL}/importer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data, format }),
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors de l\'importation:', error);
      return { success: false, message: error instanceof Error ? error.message : 'Erreur inconnue' };
    }
  }

  /**
   * Obtient les statistiques d'utilisation du module
   */
  async obtenirStatistiques(): Promise<{
    nombreUtilisations: number;
    stylesPopulaires: Array<{ style: string; utilisations: number }>;
    profilsPersonnalisesCount: number;
  } | null> {
    try {
      const response = await fetch(`${this.baseURL}/statistiques`);

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      return null;
    }
  }

  /**
   * Retourne les styles par défaut en cas d'erreur de chargement
   */
  private obtenirStylesParDefaut(): StyleBaleinePredefini[] {
    return [
      {
        id: TypeStyleBaleine.CLASSIQUE,
        nom: 'Classique',
        description: 'Style élégant et intemporel',
        apercu: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjUwIj48cGF0aCBkPSJNMCw1MCA1MCwyNSAxMDAsMCIgc3Ryb2tlPSIjMDA3YmZmIiBmaWxsPSJub25lIi8+PC9zdmc+',
        profil2D: [
          { x: 0, y: 0 },
          { x: 0.5, y: 0.5 },
          { x: 1, y: 1 }
        ]
      },
      {
        id: TypeStyleBaleine.COURBE_DOUCE,
        nom: 'Courbe Douce',
        description: 'Courbe progressive et élégante',
        apercu: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjUwIj48cGF0aCBkPSJNMCw1MCAzMCw0MCA2MCwyMCAxMDAsMCIgc3Ryb2tlPSIjMDA3YmZmIiBmaWxsPSJub25lIi8+PC9zdmc+',
        profil2D: [
          { x: 0, y: 0 },
          { x: 0.3, y: 0.2 },
          { x: 0.6, y: 0.5 },
          { x: 0.9, y: 0.8 },
          { x: 1, y: 1 }
        ]
      },
      {
        id: TypeStyleBaleine.GOTHIQUE,
        nom: 'Gothique',
        description: 'Style gothique avec montée rapide',
        apercu: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjUwIj48cGF0aCBkPSJNMCw1MCAyMCwzMCA0MCwxNSA3MCw1IDEwMCwwIiBzdHJva2U9IiMwMDdiZmYiIGZpbGw9Im5vbmUiLz48L3N2Zz4=',
        profil2D: [
          { x: 0, y: 0 },
          { x: 0.1, y: 0.3 },
          { x: 0.3, y: 0.6 },
          { x: 0.7, y: 0.9 },
          { x: 1, y: 1 }
        ]
      },
      {
        id: TypeStyleBaleine.MODERNE,
        nom: 'Moderne',
        description: 'Style contemporain et épuré',
        apercu: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjUwIj48cGF0aCBkPSJNMCw1MCA0MCw0NSA3MCwyMCA5MCw1IDEwMCwwIiBzdHJva2U9IiMwMDdiZmYiIGZpbGw9Im5vbmUiLz48L3N2Zz4=',
        profil2D: [
          { x: 0, y: 0 },
          { x: 0.4, y: 0.1 },
          { x: 0.7, y: 0.4 },
          { x: 0.9, y: 0.8 },
          { x: 1, y: 1 }
        ]
      },
      {
        id: TypeStyleBaleine.ASIATIQUE,
        nom: 'Asiatique',
        description: 'Inspiration asiatique avec courbe harmonieuse',
        apercu: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjUwIj48cGF0aCBkPSJNMCw1MCAyNSwzNSA1MCwyNSA3NSwxNSAxMDAsMCIgc3Ryb2tlPSIjMDA3YmZmIiBmaWxsPSJub25lIi8+PC9zdmc+',
        profil2D: [
          { x: 0, y: 0 },
          { x: 0.2, y: 0.4 },
          { x: 0.5, y: 0.6 },
          { x: 0.8, y: 0.8 },
          { x: 1, y: 1 }
        ]
      }
    ];
  }
}

// Instance par défaut
export const toitAPI = new ToitAPI();